import React, { useState, useEffect } from 'react';
import companyService from '../../services/companyService';
import authService from '../../services/authService';
import './CompanyUsers.css';

function CompanyUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const isAdmin = authService.isAdmin();
  const currentUser = authService.getStoredUser();

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied: Admin only');
      setLoading(false);
      return;
    }
    loadUsers();
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companyService.getUsers();

      console.log('Loaded users:', data);

      // API returns array directly: [{id, fullName, email, companyRole, ...}, ...]
      setUsers(data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (user) => {
    // Prevent changing own role
    if (user.id === currentUser.id) {
      alert('❌ You cannot change your own role!');
      return;
    }

    // API uses "companyRole" field, not "role"
    const currentRole = user.companyRole || user.role || 'USER';
    const newRole = currentRole === 'USER' ? 'ADMIN' : 'USER';

    if (!window.confirm(`Change ${user.fullName}'s role to ${newRole}?`)) {
      return;
    }

    setUpdatingUserId(user.id);

    try {
      await companyService.updateUserRole(user.id, newRole);
      alert(`✅ Role updated successfully! ${user.fullName} is now a ${newRole}.`);
      loadUsers(); // Refresh the list
    } catch (err) {
      console.error('Failed to update role:', err);
      alert('❌ Failed to update role: ' + err.message);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'ADMIN':
      case 'COMPANY_ADMIN':
        return '#059669';
      case 'USER':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getUserRole = (user) => {
    // Try multiple possible field names
    return user.companyRole || user.role || 'USER';
  };

  const getStatusBadge = (user) => {
    if (user.isActive === false) return '❌ Inactive';
    if (user.isEmailVerified === false) return '⚠️ Unverified';
    return '✅ Active';
  };

  if (!isAdmin) {
    return (
      <div className="company-users">
        <h2>User Management</h2>
        <div className="error">⛔ Access Denied: Admin only</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="company-users">
        <h2>User Management</h2>
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-users">
        <h2>User Management</h2>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="company-users">
      <h2>User Management</h2>
      <p className="users-count">
        {users.length} {users.length === 1 ? 'user' : 'users'} in your company
      </p>

      {users.length === 0 ? (
        <div className="empty-state">
          <p>No users found in your company.</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const userRole = getUserRole(user);

              return (
                <tr key={user.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {user.googleProfilePicture && (
                        <img
                          src={user.googleProfilePicture}
                          alt={user.fullName}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      )}
                      <div>
                        <div>{user.fullName}</div>
                        {user.id === currentUser.id && (
                          <span className="you-badge">You</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span
                      className="role-badge"
                      style={{ backgroundColor: getRoleBadgeColor(userRole) }}
                    >
                      {userRole}
                    </span>
                  </td>
                  <td>{getStatusBadge(user)}</td>
                  <td>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td>
                    {user.id === currentUser.id ? (
                      <span className="disabled-text">Cannot modify self</span>
                    ) : (
                      <button
                        onClick={() => handleRoleChange(user)}
                        disabled={updatingUserId === user.id}
                      >
                        {updatingUserId === user.id ? (
                          'Updating...'
                        ) : (
                          userRole === 'USER' ? '⬆️ Make Admin' : '⬇️ Make User'
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="info-box">
        <h3>💡 User Management Tips</h3>
        <ul>
          <li><strong>Admins</strong> can manage users, edit company profile, and access all features</li>
          <li><strong>Users</strong> can create jobs, upload resumes, and view candidates</li>
          <li>You cannot change your own role for security reasons</li>
          <li>All users with @medhatechnology.services emails are automatically added to this company</li>
        </ul>
      </div>
    </div>
  );
}

export default CompanyUsers;